import { css } from '@emotion/react';
import { Alert, Stack, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { SortableList } from '../../../components/SortableList/SortableList';
import { getRouteTitle, ROUTES } from '../../../constants/RouteNameMapping';
import {
  isLockedKpiSource,
  KpiSourceCatalogEntry,
  KpiSourceHierarchyEntry,
  KpiSourceId,
  pinnedKpiSources,
} from '../../../data-models/kpi-source-hierarchy.data-model';
import { ConfigStateChip } from '../components/ConfigStateChip';
import { DetailsConfigActions } from '../DataDisplay/DetailsConfigActions';
import { ExitConfigBlocker } from '../DataDisplay/ExitConfigBlocker';
import { KpiResolutionStrategyPicker } from './KpiResolutionStrategyPicker';
import { KpiSourceRow, LockedKpiSourceRow } from './KpiSourceRow';
import { useKpiSourcesConfigState } from './useKpiSourcesConfigState';

const listCss = css`
  display: flex;
  flex-direction: column;
  row-gap: 0.5rem;
`;

type SortableEntry = KpiSourceHierarchyEntry & { entry: KpiSourceCatalogEntry };

export function KpiSourcesConfig() {
  const {
    catalog,
    strategies,
    hierarchy,
    editStatus,
    isDefault,
    setStrategy,
    reorder,
    toggleSource,
    onSave,
    onReset,
  } = useKpiSourcesConfigState();

  // An API that ships no strategies has only the ranked hierarchy, so the list stays orderable.
  const strategy = strategies.find((candidate) => candidate.id === hierarchy.strategy);
  const orderable = strategy?.orderable ?? true;

  const catalogById = useMemo(() => new Map(catalog.map((entry) => [entry.id, entry])), [catalog]);
  const entries = useMemo<SortableEntry[]>(
    () =>
      hierarchy.sources.flatMap((entry) => {
        const catalogEntry = catalogById.get(entry.id);
        return catalogEntry ? [{ ...entry, entry: catalogEntry }] : [];
      }),
    [catalogById, hierarchy]
  );
  // The locked source sits above the sortable list; normalize keeps it first.
  const locked = useMemo(() => entries.filter((item) => isLockedKpiSource(item.entry)), [entries]);
  const items = useMemo(() => entries.filter((item) => !isLockedKpiSource(item.entry)), [entries]);
  // Excluded sources keep their slot but no rank: only enabled ones compete.
  const rankById = useMemo(() => {
    const ranks = new Map<KpiSourceId, number>();
    hierarchy.sources.filter((entry) => entry.enabled).forEach((entry, i) => ranks.set(entry.id, i + 1));
    return ranks;
  }, [hierarchy]);
  const enabledCount = rankById.size;
  const pinned = useMemo(() => pinnedKpiSources(catalog), [catalog]);
  const rankOf = (id: KpiSourceId) => (orderable ? (rankById.get(id) ?? 0) : null);

  const onChange = useCallback(
    (next: SortableEntry[]) => reorder(next.map(({ id, enabled }) => ({ id, enabled }))),
    [reorder]
  );

  return (
    <Stack width='100%' height='100%' padding='8px 12px 12px' overflow='auto'>
      <ExitConfigBlocker onSave={onSave} editStatus={editStatus} />
      <Stack direction='row' alignItems='start' columnGap='0.5rem' mb='1rem'>
        <Typography variant='h4'>{getRouteTitle(ROUTES.CONFIG_KPI_SOURCES)}</Typography>
        <ConfigStateChip configStatus={editStatus} />
      </Stack>
      <Stack direction='row' gap='1rem' alignItems='center' justifyContent='space-between' mb='2rem'>
        <Typography variant='body2' color='text.secondary'>
          {orderable
            ? 'When a KPI has values from more than one source, the highest-ranked source wins. Drag to ' +
              're-order, or switch a source off to leave it out entirely. Saving recomputes every affected ' +
              'KPI value.'
            : `${strategy?.description ?? ''} The order below does not apply; switch a source off to leave ` +
              'it out entirely. Saving recomputes every affected KPI value.'}
        </Typography>
        <DetailsConfigActions
          save={{ handler: onSave, disabled: editStatus !== 'modified' }}
          reset={{ handler: onReset, disabled: isDefault }}
        />
      </Stack>
      {catalog.length === 0 ? (
        <Alert severity='warning'>
          The KPI source catalog is not available on this instance, so the hierarchy cannot be configured.
        </Alert>
      ) : (
        <Stack gap='0.5rem'>
          {strategies.length > 0 && (
            <KpiResolutionStrategyPicker
              strategies={strategies}
              value={hierarchy.strategy}
              onChange={setStrategy}
            />
          )}
          <div css={listCss}>
            {locked.map((item) => (
              <LockedKpiSourceRow key={item.id} entry={item.entry} rank={rankOf(item.id)} />
            ))}
          </div>
          {orderable ? (
            <SortableList
              items={items}
              onChange={onChange}
              renderItem={(item) => (
                <KpiSourceRow
                  entry={item.entry}
                  rank={rankOf(item.id)}
                  enabled={item.enabled}
                  onToggle={toggleSource}
                  orderable
                />
              )}
            />
          ) : (
            // Not a sortable list at all, so nothing can reorder it by pointer or keyboard.
            <div css={listCss}>
              {items.map((item) => (
                <KpiSourceRow
                  key={item.id}
                  entry={item.entry}
                  rank={null}
                  enabled={item.enabled}
                  onToggle={toggleSource}
                  orderable={false}
                />
              ))}
            </div>
          )}
          <div css={listCss}>
            {pinned.map((entry, i) => (
              <LockedKpiSourceRow
                key={entry.id}
                entry={entry}
                rank={orderable ? enabledCount + i + 1 : null}
                note={
                  orderable
                    ? 'These values are derived from the sources above and cannot be re-ordered or excluded.'
                    : 'These values are derived from the sources above and cannot be excluded.'
                }
              />
            ))}
          </div>
        </Stack>
      )}
    </Stack>
  );
}
